// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useMemo } from 'react';
import {
  DataTable,
  Table,
  TableContainer,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from '@carbon/react';
import { usePlanDetailContext } from './PlanDetailContext';
import { EmptyState } from '../../../components/shell/ScreenHelpers';

const HEADERS = [
  { key: 'equipment', header: 'Equipment' },
  { key: 'phaseName', header: 'Phase' },
  { key: 'parameter', header: 'Parameter' },
  { key: 'from_value', header: 'From' },
  { key: 'to_value', header: 'To' },
  { key: 'unit', header: 'Unit' },
];

function PlanAssets() {
  const { planFile } = usePlanDetailContext();

  const { rows, equipmentCount, changeCount } = useMemo(() => {
    const rows = planFile.phases.flatMap((phase, phaseIdx) =>
      phase.equipment_changes.map((change, idx) => ({
        id: `${phaseIdx}-${idx}`,
        equipment: change.equipment,
        phaseName: phase.name,
        parameter: change.parameter,
        from_value: change.from_value,
        to_value: change.to_value,
        unit: change.unit,
      }))
    );
    const equipmentCount = new Set(rows.map(r => r.equipment)).size;
    return { rows, equipmentCount, changeCount: rows.length };
  }, [planFile.phases]);

  if (rows.length === 0) {
    return <EmptyState big="No equipment changes in this plan." />;
  }

  return (
    <div className="sched-stack">
      <DataTable rows={rows} headers={HEADERS} isSortable>
        {({ rows: tableRows, headers, getHeaderProps, getRowProps, getTableProps }: any) => (
          <TableContainer
            title="Equipment Changes"
            description={`${equipmentCount} equipment · ${changeCount} changes`}
          >
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {headers.map((header: any) => (
                    <TableHeader {...getHeaderProps({ header })} key={header.key}>
                      {header.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.map((row: any) => (
                  <TableRow {...getRowProps({ row })} key={row.id}>
                    {row.cells.map((cell: any) => (
                      <TableCell key={cell.id}>{cell.value}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataTable>
    </div>
  );
}

export default PlanAssets;
